import { forwardRef, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
// The nav entries' own icons live with the entries in config/platformNav.js
// (one distinct icon per item — the owner's rule). Only the chrome is here:
// ChevronDown for the collapsible section headers, BadgeCheck / Check for the
// profile card.
import { ChevronDown, BadgeCheck, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HeartLoader from '../HeartLoader';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../../redux/blockingSlice';
import { getPatientById } from '../../redux/patientSlice';
import { getDoctorById } from '../../redux/doctorSlice';
import { getAdminById } from '../../redux/adminSlice';
import { getMyStaffProfile } from '../../redux/staffSlice';
import { getMyPlatformProfile } from '../../redux/platformStaffSlice';
import usePermissions from '../../hooks/usePermissions';
import { getPlatformRoleName, visiblePlatformNav } from '../../utils/platformPermissions';
import { isQueueScopingOn } from '../../redux/featureSlice';
import { getDoctorSetupStatus } from '../../utils/doctorSetupStatus';
import logo from '../../assets/img/logo-TT2.png';
import getFileURL from '../../utils/fileURL';

// IS THIS NAV ENTRY THE ONE THE USER IS STANDING IN?
//
// `activePaths` can only list EXACT paths, which is fine for a screen that is
// one route. A section that owns a subtree needs `activePrefix`: Patients
// has /patients/:mocAccountId under it, and matching exactly would darken
// the menu entry the moment a patient is opened — the one time the operator most
// needs to see where they are.
const isItemActive = (item, currentPath) => {
  if (item.activePrefix && currentPath.startsWith(item.activePrefix)) return true;
  if (item.activePaths) return item.activePaths.includes(currentPath);
  return currentPath === item.path;
};

const Sidebar = forwardRef(({ isOpen, onClose }, ref) => {
  const { i18n, t } = useTranslation('sidebar');
  const { selectedPatient } = useSelector((state) => state.patient);
  const { selectedDoctor, isLoading } = useSelector((state) => state.doctor);
  const { admin } = useSelector((state) => state.admin);
  // Tenant staff: their own profile row, for the name + avatar in the rail.
  const myStaff = useSelector((state) => state.staff?.myProfile);
  // Platform staff (the Super Admin's own team): same, from /platform-staff/me.
  const myPlatformStaff = useSelector((state) => state.platformStaff?.myProfile);
  const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
  const location = useLocation();
  const userRole = localStorage.getItem('Role') || 'patient';
  const doctorId = localStorage.getItem('ID');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleProfileNavigation = (tabKey) => {
    const state = { tab: tabKey };
    const path = userRole === 'doctor'
      ? '/doctorProfile'
      : userRole === 'patient'
        ? '/patientProfile'
        : userRole === 'admin'
          ? '/adminProfile'
          : null;
    if (!path) return;
    if (resnavigate) {
      // Defer the navigation until the dirty tab's discard handler clears the block.
      dispatch(setPendingNavigation({ kind: 'route', value: path, state }));
      dispatch(tryingnavigate(true));
      return;
    }
    navigate(path, { state });
    if (window.innerWidth <= 1024 && typeof onClose === 'function') {
      onClose();
    }
  };

  // COLLAPSIBLE SECTIONS. Every section starts OPEN — the owner's call: a
  // menu that must be opened before it can be read hides the product. A
  // header click still collapses/expands, and that choice sticks per section.
  // Keyed by section id — undefined means "open".
  const [openSections, setOpenSections] = useState({});
  const isSectionOpen = (section) => {
    if (section.id in openSections) return openSections[section.id];
    return true;
  };
  const toggleSection = (section) => {
    const currentlyOpen = isSectionOpen(section);
    setOpenSections((prev) => ({ ...prev, [section.id]: !currentlyOpen }));
  };
  const [imageError, setImageError] = useState(false);

  // Reset image error
  useEffect(() => {
    setImageError(false);
  }, [selectedDoctor, selectedPatient, admin]);

  // Load user profile
  useEffect(() => {
    if (!selectedPatient && userRole === 'patient' && doctorId) {
      dispatch(getPatientById(doctorId));
    } else if (!selectedDoctor && userRole === 'doctor' && doctorId) {
      dispatch(getDoctorById(doctorId));
    } else if (!admin && userRole === 'admin' && doctorId) {
      dispatch(getAdminById(doctorId));
    } else if (!myStaff && userRole === 'user') {
      // Keyed on the token, not on an id from storage — one employee must not be
      // able to load another's profile by editing localStorage.
      dispatch(getMyStaffProfile());
    } else if (!myPlatformStaff && userRole === 'platform_staff') {
      // Same token-keyed read for the platform's own team.
      dispatch(getMyPlatformProfile());
    }
  }, [userRole, doctorId, dispatch, selectedPatient, selectedDoctor, admin, myStaff, myPlatformStaff]);

  // User info
  const userName =
    userRole === 'doctor'
      ? selectedDoctor?.name || t('sidebar.doctors')
      : userRole === 'patient'
        ? selectedPatient?.name || t('sidebar.patients')
        : userRole === 'admin'
          ? admin?.name || t('sidebar.admins')
          : userRole === 'user'
            ? myStaff?.name || t('sidebar.staff', 'Staff')
            : userRole === 'platform_staff'
              ? [myPlatformStaff?.name, myPlatformStaff?.surname].filter(Boolean).join(' ') || t('sidebar.platform_staff', 'Platform staff')
              : t('sidebar.guest');

  const user = {
    name: userName,
    image:
      userRole === 'doctor' ? selectedDoctor?.image || ''
        : userRole === 'admin' ? admin?.image || ''
          : userRole === 'patient' ? selectedPatient?.image || ''
            : userRole === 'user' ? myStaff?.image || ''
              : userRole === 'platform_staff' ? myPlatformStaff?.image || ''
                : '',
  };

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    if (userRole === 'patient' && selectedPatient) {
      const fields = [
        'name', 'surname', 'gender', 'dob', 'email', 'phone',
        'address', 'country', 'city', 'zipCode', 'occupation', 'image'
      ];
      const filledFields = fields.filter(field => {
        const value = selectedPatient[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });
      return Math.round((filledFields.length / fields.length) * 100);
    } else if (userRole === 'doctor' && selectedDoctor) {
      const fields = [
        'name', 'surname', 'gender', 'dob', 'email', 'phone',
        'licenseNumber', 'description', 'image'
      ];
      const filledFields = fields.filter(field => {
        const value = selectedDoctor[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });

      // Check websiteLink array - must have at least 1 item
      const hasWebsiteLink = selectedDoctor.websiteLink &&
        Array.isArray(selectedDoctor.websiteLink) &&
        selectedDoctor.websiteLink.length >= 1;

      // Check certification array - must have at least 1 item
      const hasCertification = selectedDoctor.certification &&
        Array.isArray(selectedDoctor.certification) &&
        selectedDoctor.certification.length >= 1;

      // Total fields = regular fields (9) + websiteLink (1) + certification (1) = 11
      const totalFields = fields.length + 2;
      const completedFields = filledFields.length + (hasWebsiteLink ? 1 : 0) + (hasCertification ? 1 : 0);

      return Math.round((completedFields / totalFields) * 100);
    } else if (userRole === 'admin' && admin) {
      const fields = ['name', 'email', 'image'];
      const filledFields = fields.filter(field => {
        const value = admin[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });
      return Math.round((filledFields.length / fields.length) * 100);
    }
    return 0;
  };

  const profileCompletion = calculateProfileCompletion();

  // Get progress bar color based on completion percentage
  const getProgressColor = (percentage) => {
    if (percentage < 34) {
      // Red for low completion (0-33%)
      return 'bg-gradient-to-r from-red-500 to-red-600';
    } else if (percentage < 67) {
      // Yellow/Orange for medium completion (34-66%)
      return 'bg-gradient-to-r from-yellow-500 to-orange-500';
    } else {
      // Green for high completion (67-100%)
      return 'bg-gradient-to-r from-emerald-400 to-emerald-600';
    }
  };

  // Get percentage text color based on completion
  const getPercentageTextColor = (percentage) => {
    if (percentage < 34) {
      return 'text-red-600';
    } else if (percentage < 67) {
      return 'text-orange-500';
    } else {
      return 'text-green-600';
    }
  };

  // Get doctor setup status using utility function
  const setupStatus = getDoctorSetupStatus(userRole, selectedDoctor);

  const isProfile100 = profileCompletion === 100;
  const isSetup100 = setupStatus ? (setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4 && setupStatus.point5) : (userRole !== 'doctor');
  const showBadge = userRole === 'doctor' ? (isSetup100) : (isProfile100 && isSetup100);


  // super_admin is normalised to Role='admin' at login; this flag is the only
  // client-side distinction, and it is also the label the profile card shows.
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  // PLATFORM STAFF — the Super Admin's own team. What they see is decided by
  // their role's permissions (utils/platformPermissions.js), never by a flag.
  const isPlatformStaff = userRole === 'platform_staff';

  // WHAT THIS PERSON MAY DO. Only meaningful for staff; kept for the profile
  // label below. This portal signs in the super admin and their platform staff.
  const { roleName: staffRoleName } = usePermissions();

  // Whether support.queue.<queue> still counts (Settings → Support → "Queue-
  // based agent access"). Read from the store, not the localStorage mirror, so
  // a toggle re-draws the rail at once.
  const queueScopingOn = useSelector(isQueueScopingOn);

  // THE MENU. One manifest for both personas (config/platformNav.js), filtered
  // by one rule (canOpenNavItem): the Super Admin passes everything, a platform
  // staff member sees exactly the entries their role's permissions open, and a
  // section with nothing left is dropped rather than drawn with a "0" badge.
  // Labels are resolved here because the manifest cannot call t(). Two
  // hand-kept rails (one per persona) is what this replaced — they drifted,
  // and a role granted every permission still saw only the support queues.
  const navSections = visiblePlatformNav(queueScopingOn).map((section) => ({
    id: section.id,
    title: t(section.labelKey, section.defaultLabel),
    items: section.items.map((item) => ({
      id: item.id,
      path: item.path,
      icon: item.icon,
      name: t(item.labelKey, item.defaultLabel),
      activePaths: item.activePaths,
      activePrefix: item.activePrefix,
    })),
  }));

  // ROLES ARE ENUMERATED, NEVER FALLEN THROUGH. The literal 'admin' role (which
  // the super admin is normalised to) and 'platform_staff' get this menu; any
  // other role gets an empty rail, which is loud enough to be fixed the same day.
  const isAdmin = userRole === 'admin' || isPlatformStaff;
  // Flat run of every VISIBLE item, for the collapsed icon rail (no section
  // headers fit a 16px rail).
  const railItems = navSections.flatMap((s) => s.items);

  const handleNavClick = (e, targetPath) => {
    if (resnavigate) {
      e.preventDefault();
      // Remember where the user wanted to go so the dirty tab can auto-redirect
      // to it after the user confirms Discard.
      if (targetPath) {
        dispatch(setPendingNavigation({ kind: 'route', value: targetPath }));
      }
      dispatch(tryingnavigate(true));
      return;
    }
    if (window.innerWidth <= 1024) onClose();
  };

  // After a dirty tab confirms Discard, `resnavigate` flips back to false while
  // `pendingNavigation` still holds the original destination. Auto-perform the
  // route navigation here so the user lands where they originally clicked.
  useEffect(() => {
    if (!resnavigate && pendingNavigation?.kind === 'route' && pendingNavigation.value) {
      navigate(pendingNavigation.value, pendingNavigation.state ? { state: pendingNavigation.state } : undefined);
      dispatch(clearPendingNavigation());
      if (window.innerWidth <= 1024 && typeof onClose === 'function') {
        onClose();
      }
    }
  }, [resnavigate, pendingNavigation, navigate, dispatch, onClose]);

  // Tooltip position state — used only by the collapsed desktop icon rail.
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, visible: false, text: '' });
  const tooltipRef = useRef(null);
  const navRef = useRef(null);

  const handleTooltipShow = (e, text) => {
    if (!isOpen && window.innerWidth >= 1024) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 16,
        visible: true,
        text: text
      });
    }
  };

  const handleTooltipHide = () => {
    setTooltipPosition(prev => ({ ...prev, visible: false }));
  };

  // Update tooltip position on scroll
  useEffect(() => {
    if (!isOpen && window.innerWidth >= 1024 && tooltipPosition.visible && navRef.current) {
      const handleScroll = () => {
        setTooltipPosition(prev => ({ ...prev, visible: false }));
      };
      navRef.current.addEventListener('scroll', handleScroll);
      return () => {
        if (navRef.current) {
          navRef.current.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [tooltipPosition.visible, isOpen]);

  // A single nav item, rendered as the mockup's .item — teal→deep gradient pill
  // with the pill shadow when active, muted row otherwise.
  const renderNavLink = (item) => {
    const active = isItemActive(item, location.pathname);
    const Icon = item.icon;
    return (
      <NavLink
        key={item.id}
        to={item.path}
        data-testid={item.id}
        onClick={(e) => handleNavClick(e, item.path)}
        className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors ${active
          ? 'text-white'
          : 'text-gray-600 hover:bg-[#4ABDBD]/10 hover:text-[#2D8A8A]'
          }`}
      >
        {active && (
          <motion.div
            layoutId="sa-sidebar-active"
            className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] shadow-[0_8px_20px_-10px_rgba(45,138,138,0.55)]"
            initial={false}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
          />
        )}
        <Icon
          data-testid={`nav-link-icon-${item.id}`}
          className={`relative z-10 h-[18px] w-[18px] flex-shrink-0 transition-colors ${active ? 'text-white' : 'text-gray-400 group-hover:text-[#2D8A8A]'}`}
        />
        <span className="relative z-10 min-w-0 flex-1 truncate">{item.name}</span>
      </NavLink>
    );
  };

  // A collapsible section: uppercase mono header with the item count + chevron,
  // then its items. Clicking the header toggles it.
  const renderSection = (section) => {
    const open = isSectionOpen(section);
    return (
      <div key={section.id} className="mt-1">
        <button
          type="button"
          data-testid={`${section.id}-header`}
          onClick={() => toggleSection(section)}
          className="flex w-full items-center gap-2 px-2.5 pb-1.5 pt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-gray-400 transition-colors hover:text-[#2D8A8A]"
        >
          <span className="truncate">{section.title}</span>
          <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-gray-400">{section.items.length}</span>
          <ChevronDown className={`ml-auto h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
        </button>
        {open && (
          <div data-testid={`${section.id}-items`} className="flex flex-col gap-0.5">
            {section.items.map(renderNavLink)}
          </div>
        )}
      </div>
    );
  };

  // The collapsed desktop rail: icon-only, tooltip on hover, active pill.
  const renderRailItem = (item) => {
    const active = isItemActive(item, location.pathname);
    const Icon = item.icon;
    return (
      <NavLink
        key={item.id}
        to={item.path}
        data-testid={item.id}
        onClick={(e) => handleNavClick(e, item.path)}
        onMouseEnter={(e) => handleTooltipShow(e, item.name)}
        onMouseLeave={handleTooltipHide}
        className={`group relative flex items-center justify-center rounded-xl py-3 transition-colors ${active ? 'text-white' : 'text-gray-500 hover:bg-[#4ABDBD]/10 hover:text-[#2D8A8A]'}`}
      >
        {active && (
          <motion.div
            layoutId="sa-sidebar-active-rail"
            className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]"
            initial={false}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
          />
        )}
        <Icon className="relative z-10 h-5 w-5 flex-shrink-0" />
      </NavLink>
    );
  };

  return (
    <div
      ref={ref}
      className={`fixed inset-y-0 left-0 z-50 bg-white transform transition-all duration-300 shadow-lg flex flex-col h-screen overflow-y-hidden overflow-x-hidden
        ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0 lg:-translate-x-0 lg:w-16 '} lg:static`}
    >
      {/* Header — brand heart + plain-teal wordmark; collapsed rail shows the
          short "MOC" mark since the full name has no room. */}
      <div className={`flex items-center py-3 ${isOpen ? 'px-4 gap-2' : 'flex-col justify-center px-1 gap-1'}`}>
        <img
          src={logo}
          alt="Mitra One Care"
          className={`object-contain flex-shrink-0 ${isOpen ? 'h-10 w-10' : 'h-9 w-9'}`}
        />
        {isOpen ? (
          <span className="text-xl font-bold leading-none text-[#4ABDBD] whitespace-nowrap">Mitra One Care</span>
        ) : (
          <span className="text-[9px] font-extrabold tracking-widest text-[#2D8A8A]">MOC</span>
        )}
      </div>

      {/* USER CARD — avatar initial on a teal gradient, name, and a role pill,
          the same treatment the patient app uses. Only rendered when the sidebar
          is expanded. */}
      {isOpen && (
        <div
          data-testid="sidebar-profile-section"
          className={`relative flex flex-col bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 rounded-2xl border border-[#4ABDBD]/20 mb-4 py-3 gap-2 mt-2 px-3 flex-shrink-0 mx-4`}
        >
          {showBadge && (
            <BadgeCheck className="absolute top-2 right-2 w-6 h-6 text-white" fill="#10B981" />
          )}
          <div className="flex items-center gap-3">
            {isLoading ? (
              <HeartLoader data-testid="profile-loading" />
            ) : (user.image && user.image.trim() !== '' && !imageError) ? (
              <img
                data-testid="profile-image"
                src={getFileURL(user.image)}
                alt={user.name}

                className="w-12 h-12 rounded-full object-cover border-2 border-[#4ABDBD]/20 shadow-md"
                onError={() => setImageError(true)}
              />
            ) : (
              <div
                data-testid="profile-initial"
                className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-full shadow-lg text-white font-bold text-xl border-2 border-white"
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            {isOpen && !isLoading && (
              <div data-testid="profile-info" className="flex flex-col flex-1">
                <span data-testid="profile-name" className="font-bold text-gray-800 text-sm">
                  {user.name}
                </span>
                <span
                  data-testid="profile-role"
                  className="text-[#3A9B9B] text-xs w-fit font-medium bg-white px-2 py-1 rounded-lg shadow"
                >
                  {/* super_admin is normalised to Role='admin'; show its real label.
                      Platform staff show their platform ROLE name ("Support Agent"). */}
                  {isSuperAdmin
                    ? t('sidebar.super_admin', 'Super Admin')
                    : isPlatformStaff
                      ? (myPlatformStaff?.roleName || getPlatformRoleName() || t('sidebar.platform_staff', 'Platform staff'))
                      : userRole === 'user'
                        ? (staffRoleName || t('sidebar.staff', 'Staff'))
                        : t(`sidebar.${userRole}s`)}
                </span>
              </div>
            )}
          </div>

          {/* Profile Completion Progress */}
          {isOpen && !isLoading && profileCompletion < 100 && userRole !== 'doctor' && userRole !== 'admin' && !isPlatformStaff && (
            <div
              data-testid="sidebar-profile-completion"
              className="space-y-1.5 shadow bg-white rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleProfileNavigation('profile')}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700">{t('sidebar.profile_completion')}</span>
                <span className={`text-[11px] font-bold ${getPercentageTextColor(profileCompletion)}`}>{profileCompletion}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${getProgressColor(profileCompletion)} shadow-sm`}
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
          )}

          {/* Doctor Setup Flow - Only for Doctors */}
          {isOpen && !isLoading && userRole === 'doctor' && setupStatus && !(setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4) && (
            <div className="shadow bg-white rounded-lg p-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700">{t('sidebar.setup_progress')}</span>
                <span className="text-[11px] font-bold text-[#4ABDBD]">{setupStatus.point4 ? '100%' : setupStatus.point3 ? '75%' : setupStatus.point2 ? '50%' : setupStatus.point1 ? '25%' : '0%'}</span>
              </div>

              <div className="relative pt-2">
                {/* Connecting Line */}
                <div className="absolute top-3.5 left-6 right-6 h-1 bg-gray-300">
                  <div
                    className={`h-full transition-all duration-500 ${setupStatus.point4 ? 'bg-[#4ABDBD]' :
                      setupStatus.point3 ? 'bg-[#4ABDBD]' :
                        setupStatus.point2 ? 'bg-[#4ABDBD]' :
                          setupStatus.point1 ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                      }`}
                    style={{
                      width: setupStatus.point4 ? '100%' :
                        setupStatus.point3 ? '75%' :
                          setupStatus.point2 ? '50%' :
                            setupStatus.point1 ? '25%' : '0%'
                    }}
                  />
                </div>

                {/* Points Container */}
                <div className="relative flex items-center justify-between px-2">
                  {/* Point 1: Profile Complete */}
                  <div
                    data-testid="sidebar-setup-point-profile"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('profile')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point1
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point1 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">P</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point1 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>P</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_profile')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 2: Appointment Speciality & Type */}
                  <div
                    data-testid="sidebar-setup-point-specialities"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('specialities')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point2
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point2 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">S</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point2 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>S</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_speciality')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 3: Address */}
                  <div
                    data-testid="sidebar-setup-point-address"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('address')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point3
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point3 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">A</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point3 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>AD</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_address')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 4: Availability */}
                  <div
                    data-testid="sidebar-setup-point-availability"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('availability')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point4
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point4 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">A</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point4 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>AV</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_availability')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav ref={navRef} className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isOpen ? 'ml-3 pr-3 pb-3' : 'px-2 space-y-1'}`} style={{ position: 'relative' }}>
        {isAdmin && (
          isOpen ? (
            navSections.map(renderSection)
          ) : (
            railItems.map(renderRailItem)
          )
        )}
      </nav>

      {/* Footer */}
      {isOpen && (
        <footer className="p-4 border-t border-gray-200 text-center text-[9px] text-gray-500">
          <p>{t('sidebar.footer_copyright', { year: new Date().getFullYear() })}</p>
          <p className="mt-1">
            <span>{t('sidebar.app_version')}</span>{' '}
            <span className="text-[#4ABDBD] font-semibold">{t('sidebar.powered_by')}</span>
          </p>
        </footer>
      )}

      {/* Fixed Tooltip via Portal */}
      {!isOpen && window.innerWidth >= 1024 && tooltipPosition.visible && typeof document !== 'undefined' && createPortal(
        <div
          ref={tooltipRef}
          className="fixed p-3 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white text-sm rounded-xl pointer-events-none shadow-xl whitespace-nowrap z-[99999]"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
          }}
        >
          {tooltipPosition.text}
        </div>,
        document.body
      )}
    </div>
  );
});

export default Sidebar;
